import { Box } from "@mui/material";
import ConfirmDialog from "components/ConfirmDialog";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useEffect, useState, useRef } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { ResponseOfAdminContentItem, SaveContentRequest, SaveResponse } from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import translateError from "utils/translateError";
import { ContentBlockForm } from "./ContentBlockForm";

export function ContentBlockUpdateForm() {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const { contentService, contentBlocksUrl, contentBlockDetailsUrl } = useGlobalContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { organizationId, organizationName } = contextStore.useState();

  const [confirmSaveOpen, confirmSaveOpenActions] = useBoolean(false);
  const [pendingValues, setPendingValues] = useState<SaveContentRequest | null>(null);
  const initialOrgIdRef = useRef(organizationId);
  const contentBlockNameRef = useRef<string | null>(null);
  const isInitialLoadRef = useRef(true);

  const contentBlockResponse = useStrictQuery<ResponseOfAdminContentItem>(
    ["contentService.getContentById", id, organizationId],
    async () => {
      if (!isInitialLoadRef.current && contentBlockNameRef.current) {
        return await contentService.getContentByName(contentBlockNameRef.current);
      }

      const response = await contentService.getContentById(id!);
      if (response?.data?.name && !contentBlockNameRef.current) {
        contentBlockNameRef.current = response.data.name;
      }
      isInitialLoadRef.current = false;
      return response;
    },
    {
      enabled: !!id,
      staleTime: 0,
    }
  );

  useEffect(() => {
    if (organizationId !== initialOrgIdRef.current) {
      navigate(contentBlockDetailsUrl(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const contentBlock = contentBlockResponse.data?.data;

  useTitle(contentBlock?.name || t({ id: "editContentBlock" }));

  const isInherited = contentBlock?.inheritedOrganizationName !== organizationName;

  const onCancel = () => {
    navigate(contentBlocksUrl());
  };

  const handleSubmit = async (values: SaveContentRequest) => {
    if (isInherited) {
      setPendingValues(values);
      confirmSaveOpenActions.setTrue();
      return;
    }
    return await performSave(values, false);
  };

  const handleConfirmSave = async () => {
    confirmSaveOpenActions.setFalse();
    if (pendingValues) {
      return await performSave(pendingValues, true);
    }
  };

  const performSave = async (values: SaveContentRequest, isCreatingOverride: boolean) => {
    try {
      const updated: SaveResponse = await registerPromise(
        contentService.saveContentItem(
          SaveContentRequest.fromJS({
            ...values,
            id: isCreatingOverride ? null : contentBlock?.id,
            organizationId: organizationId,
          })
        )
      );

      queryClient.invalidateQueries(["contentService.getContentBlocks"]);
      queryClient.invalidateQueries(["contentService.getContentById"]);
      queryClient.invalidateQueries(["contentService.getDescendantContentBlocks"]);

      navigate(contentBlocksUrl());

      return updated;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  if (contentBlockResponse.isLoading) return <Loader />;

  if (!contentBlock) {
    navigate(contentBlocksUrl());
    return null;
  }

  return (
    <>
      <Box my={2}>
        <RedirectBackButton
          redirectRoute={contentBlocksUrl()}
          labelKey="backToListButtonLabel"
        />
      </Box>

      <ContentBlockForm
        key={`${contentBlock?.id}-${organizationId}`}
        onSubmit={handleSubmit}
        contentBlock={contentBlock}
        submitLabel={t({ id: "editContentBlock" })}
        onCancel={onCancel}
      />

      <ConfirmDialog
        dialogTitleKey="areYouSure"
        rawContent={t(
          { id: "settingSaveConfirmationText" },
          { organization: organizationName }
        )}
        open={confirmSaveOpen}
        onCancel={confirmSaveOpenActions.setFalse}
        handleConfirm={handleConfirmSave}
      />
    </>
  );
}
